import {Component, OnDestroy, OnInit} from '@angular/core';
import {CustomerService} from '../../../services/customer.service';
import {CustomerModel} from '../../../models/customer.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EMAIL_PATTERN, markFormGroupTouched, PHONE_MASK, PHONE_PATTERN, SNACK_CONFIG} from '../../../global';
import {MatDialog, MatDialogConfig, MatSnackBar} from '@angular/material';
import {PictureDialogComponent} from '../../../components/picture-dialog/picture-dialog.component';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import * as _ from 'lodash';
import {SharedService} from '../../../services/shared.service';
import {CodeVerificationDialogComponent} from '../../../components/code-verification-dialog/code-verification-dialog.component';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  customer: CustomerModel;
  background: string = '#e6e6e6';
  showEditFrom: boolean;
  showLoader: boolean;
  editForm: FormGroup;
  phoneMask = PHONE_MASK;

  constructor(private customerService: CustomerService,
              private fb: FormBuilder,
              private shared: SharedService,
              private dialog: MatDialog,
              private snackBar: MatSnackBar) {
    this.refreshCustomer();
    this.editForm = this.fb.group({
      displayName: [null, Validators.required],
      phone: [null, [Validators.required, Validators.pattern(PHONE_PATTERN)]],
      email: [null, [Validators.required, Validators.pattern(EMAIL_PATTERN)]]
    });
  }

  ngOnInit() {

  }

  editProfile() {
    this.editForm.patchValue({displayName: this.customer.displayName, phone: this.customer.phone, email: this.customer.email});
    this.showEditFrom = true;
  }

  saveUpdates(form: FormGroup) {
    markFormGroupTouched(form);
    if (form.valid) {
      this.showLoader = true;
      let data = _.clone(form.value);
      let phone = data.phone.match(/\d/g).join('');
      delete data.phone;
      this.customerService.updateCustomerProfile(data)
        .then(res => {
          if (phone != this.customer.phone) {
            this.sendVerificationCode(phone);
          } else {
            setTimeout(() => {
              this.snackBar.open('Profile updated', null, SNACK_CONFIG);
              this.showLoader = false;
              this.cancelEdit();
            }, 2000);
            this.refreshCustomer(true);
          }
        })
        .catch(err => {
          this.shared.openErrorDialog({title: 'Error', body: err.error.errorMessage || 'Unexpected error. Please try again'});
          this.showLoader = false;
        });
    }
  }

  sendVerificationCode(phone: string) {
    this.customerService.sendSms('+1', phone)
      .then(res => {
        if (res.isSent) {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.autoFocus = true;
          dialogConfig.disableClose = true;
          dialogConfig.data = {
            phone: phone
          };
          let dialog = this.dialog.open(CodeVerificationDialogComponent, dialogConfig);
          dialog.afterClosed()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
              if (data && data.code) this.changePhone(phone, data.code);
              else this.showLoader = false;
            });
        }
      });
  }

  changePhone(phone: string, code: string) {
    this.customerService.changePhone(code, phone)
      .then(res => {
        this.showLoader = false;
        this.refreshCustomer(true);
        this.cancelEdit();
        this.snackBar.open('Profile updated', null, SNACK_CONFIG);
      })
      .catch(err => {
        this.shared.openErrorDialog({title: 'Error', body: err.error.errorMessage || 'Unexpected error. Please try again'});
        this.showLoader = false;
      });
  }

  picturesDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    let dialog = this.dialog.open(PictureDialogComponent, dialogConfig);
    dialog.afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(_ => {
        this.refreshCustomer();
      });
  }

  refreshCustomer(force?: boolean) {
    this.customerService.getCurrentCustomer(force)
      .then(customer => {
        this.customer = customer;
        this.shared.sendEvent({type: 'profile_refresh'});
        if (this.customer.pictureURL) this.background = 'url(' + this.customer.pictureURL + ') center center no-repeat';
      });
  }

  cancelEdit() {
    this.editForm.reset();
    this.showEditFrom = false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
